import { FormEvent, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { ApiKeyScope } from '@/api/generated/client';
import { useAuth } from '@/hooks/use-auth';

export const API_KEY_PAGE_SIZE = 25;

export type ScopeGroup = {
  label: string;
  read: ApiKeyScope;
  readLabel?: string;
  write?: ApiKeyScope;
  readScopes?: ApiKeyScope[];
  writeScopes?: ApiKeyScope[];
};

export const SCOPE_GROUPS: ScopeGroup[] = [
  { label: 'Claims', read: ApiKeyScope.ClaimsRead, write: ApiKeyScope.ClaimsWrite },
  { label: 'Documents', read: ApiKeyScope.DocumentsRead, write: ApiKeyScope.DocumentsWrite },
  { label: 'Photos', read: ApiKeyScope.PhotosRead, write: ApiKeyScope.PhotosWrite },
  { label: 'Notes', read: ApiKeyScope.NotesRead, write: ApiKeyScope.NotesWrite },
  { label: 'Tasks', read: ApiKeyScope.TasksRead, write: ApiKeyScope.TasksWrite },
  { label: 'Links', read: ApiKeyScope.LinksRead, write: ApiKeyScope.LinksWrite },
  { label: 'Financials', read: ApiKeyScope.FinancialsRead, write: ApiKeyScope.FinancialsWrite },
  { label: 'Forms', read: ApiKeyScope.FormsRead, write: ApiKeyScope.FormsWrite },
  { label: 'Contacts', read: ApiKeyScope.ContactsRead, write: ApiKeyScope.ContactsWrite },
  { label: 'Organizations', read: ApiKeyScope.OrganizationsRead, write: ApiKeyScope.OrganizationsWrite },
  { label: 'Properties', read: ApiKeyScope.PropertiesRead, write: ApiKeyScope.PropertiesWrite },
  { label: 'Notifications', read: ApiKeyScope.NotificationsRead, write: ApiKeyScope.NotificationsWrite },
  { label: 'Workspace', read: ApiKeyScope.WorkspaceRead, write: ApiKeyScope.WorkspaceWrite },
  { label: 'Emails', read: ApiKeyScope.EmailsRead },
  { label: 'Activity', read: ApiKeyScope.ActivityRead },
  { label: 'Reports', read: ApiKeyScope.ReportsRead },
  { label: 'Comparisons', read: ApiKeyScope.ComparisonsRead },
  { label: 'Supplements', read: ApiKeyScope.SupplementsRead },
  { label: 'Estimates', read: ApiKeyScope.EstimatesRead },
];

export const DEFAULT_API_KEY_SCOPES = [...new Set(
  SCOPE_GROUPS.flatMap((group) => [
    ...(group.readScopes ?? [group.read]),
    ...(group.writeScopes ?? (group.write ? [group.write] : [])),
  ]),
)];

export function useApiKeySettings() {
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === 'admin' || user?.role === 'owner';

  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<ApiKeyScope[]>(DEFAULT_API_KEY_SCOPES);
  const [expiresAt, setExpiresAt] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [generatedKeyId, setGeneratedKeyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [companyPage, setCompanyPage] = useState(1);

  const { data, isLoading, isError: isApiKeysError, refetch } = useQuery({
    queryKey: ['api-keys', user?.id, page],
    queryFn: () => apiClient.apiKeys.listApiKeys({ page, limit: API_KEY_PAGE_SIZE }),
    enabled: user?.id !== undefined,
    placeholderData: keepPreviousData,
  });

  const {
    data: companyData,
    isLoading: isCompanyLoading,
    isError: isCompanyApiKeysError,
    refetch: refetchCompany,
  } = useQuery({
    queryKey: ['api-keys', 'company', user?.id, user?.companyId, companyPage],
    queryFn: () => apiClient.apiKeys.listCompanyApiKeys({ page: companyPage, limit: API_KEY_PAGE_SIZE }),
    enabled: isCompanyAdmin && user?.id !== undefined && user?.companyId !== undefined,
    placeholderData: keepPreviousData,
  });

  const apiKeys = data?.apiKeys ?? [];
  const companyApiKeys = companyData?.apiKeys ?? [];
  const apiKeysTotal = data?.total ?? 0;
  const companyApiKeysTotal = companyData?.total ?? 0;

  const toggleReadScopes = (group: ScopeGroup) => {
    const scopes = group.readScopes ?? [group.read];
    setSelectedScopes((prev) => {
      const hasAllScopes = scopes.every((scope) => prev.includes(scope));
      return hasAllScopes
        ? prev.filter((scope) => !scopes.includes(scope))
        : [...new Set([...prev, ...scopes])];
    });
  };

  const toggleWriteScopes = (group: ScopeGroup) => {
    const scopes = group.writeScopes ?? (group.write ? [group.write] : []);
    if (scopes.length === 0) return;
    setSelectedScopes((prev) => {
      const hasAllScopes = scopes.every((scope) => prev.includes(scope));
      return hasAllScopes
        ? prev.filter((scope) => !scopes.includes(scope))
        : [...new Set([...prev, ...scopes])];
    });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (selectedScopes.length === 0) {
        throw new Error('Select at least one scope.');
      }

      return apiClient.apiKeys.createApiKey({
        createApiKeyRequest: {
          name: name.trim(),
          scopes: selectedScopes,
          expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`) : null,
        },
      });
    },
    onSuccess: async (created) => {
      setGeneratedKey(created.key);
      setGeneratedKeyId(created.id);
      setName('');
      setSelectedScopes(DEFAULT_API_KEY_SCOPES);
      setExpiresAt('');
      setError(null);
      // Lists are newest-first, so jump to page 1 where the new key appears.
      if (page !== 1) {
        setPage(1);
      } else {
        await refetch();
      }
      if (isCompanyAdmin) {
        if (companyPage !== 1) {
          setCompanyPage(1);
        } else {
          await refetchCompany();
        }
      }
    },
    onError: (mutationError) => {
      const fallback = 'Unable to create API key. Please try again.';
      setError(mutationError instanceof Error ? mutationError.message : fallback);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (apiKeyId: number) => apiClient.apiKeys.revokeApiKey({ apiKeyId }),
    onSuccess: async (_data, apiKeyId) => {
      setError(null);
      // The plaintext is only ever shown once; once its key is revoked, stop
      // displaying/allowing copy of the now-dead value.
      if (generatedKeyId === apiKeyId) {
        setGeneratedKey('');
        setGeneratedKeyId(null);
      }
      // If the revoked key was the last item on a trailing page, step back so
      // the list doesn't land on an empty page.
      if (apiKeys.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await refetch();
      }
      if (isCompanyAdmin) {
        if (companyApiKeys.length === 1 && companyPage > 1) {
          setCompanyPage(companyPage - 1);
        } else {
          await refetchCompany();
        }
      }
    },
    onError: (mutationError) => {
      const fallback = 'Unable to revoke API key. Please try again.';
      setError(mutationError instanceof Error ? mutationError.message : fallback);
    },
  });

  const createDisabled = useMemo(
    () => createMutation.isPending || name.trim().length === 0 || selectedScopes.length === 0,
    [createMutation.isPending, name, selectedScopes],
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<boolean> => {
    event.preventDefault();
    setError(null);

    try {
      await createMutation.mutateAsync();
      return true;
    } catch {
      // Error state is handled in onError.
      return false;
    }
  };

  const handleRevoke = async (apiKeyId: number) => {
    setError(null);

    try {
      await revokeMutation.mutateAsync(apiKeyId);
    } catch {
      // Error state is handled in onError.
    }
  };

  return {
    name,
    setName,
    selectedScopes,
    toggleReadScopes,
    toggleWriteScopes,
    expiresAt,
    setExpiresAt,
    generatedKey,
    generatedKeyId,
    error,
    isLoading,
    isApiKeysError,
    refetch,
    apiKeys,
    apiKeysTotal,
    page,
    setPage,
    isCompanyAdmin,
    companyApiKeys,
    companyApiKeysTotal,
    companyPage,
    setCompanyPage,
    isCompanyLoading,
    isCompanyApiKeysError,
    refetchCompany,
    currentUserId: user?.id ?? null,
    createDisabled,
    isCreating: createMutation.isPending,
    handleSubmit,
    handleRevoke,
    revokingKeyId: revokeMutation.isPending ? revokeMutation.variables : null,
  };
}
